import {
  createTheme,
  rem,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from '@mantine/core';
import { FONTS, VIOLET } from '@quizee/core';

// The workspace shares the marketing site's paper, ink and vermilion identity.
// Publisher widget themes remain independent: changing the tool never changes an embed.
const vermilion: MantineColorsTuple = [
  '#fff2ea',
  '#ffe2d3',
  '#f7c2a9',
  '#ed9f7f',
  '#de7958',
  '#d65b39',
  '#c94327',
  '#af371f',
  '#922d1b',
  '#752517',
];
const sage: MantineColorsTuple = [
  '#f2f5ec',
  '#e5ebdb',
  '#cbd7ba',
  '#b0c29b',
  '#91ab7b',
  '#77945f',
  '#5e7b49',
  '#4c643d',
  '#3c5132',
  '#2b3e27',
];
const neutral: MantineColorsTuple = [
  '#fafaf6',
  '#f1f2eb',
  '#e4e6dc',
  '#d4d8ca',
  '#b5bcaa',
  '#929b85',
  '#6a745e',
  '#515b46',
  '#37412e',
  '#232d1b',
];
const dark: MantineColorsTuple = [
  '#e6eadf',
  '#c9d0c1',
  '#acb6a1',
  '#88967b',
  '#55624a',
  '#394533',
  '#2b3527',
  '#212a1e',
  '#192117',
  '#12190f',
];

export const theme = createTheme({
  primaryColor: 'vermilion',
  primaryShade: { light: 6, dark: 6 },
  colors: { vermilion, coral: vermilion, sage, violet: [...VIOLET], gray: neutral, dark },
  white: '#fffefa',
  black: '#232820',
  fontFamily: FONTS.body,
  fontFamilyMonospace: FONTS.mono,
  fontSizes: { xs: rem(12), sm: rem(14), md: rem(14), lg: rem(16), xl: rem(19) },
  headings: {
    fontFamily: FONTS.display,
    fontWeight: '500',
    sizes: {
      h1: { fontSize: rem(32), lineHeight: '1.2' },
      h2: { fontSize: rem(28), lineHeight: '1.2' },
      h3: { fontSize: rem(20), lineHeight: '1.3' },
      h4: { fontSize: rem(17), lineHeight: '1.4' },
      h5: { fontSize: rem(15), lineHeight: '1.4' },
      h6: { fontSize: rem(14), lineHeight: '1.4' },
    },
  },
  radius: { xs: rem(3), sm: rem(5), md: rem(8), lg: rem(12), xl: rem(16) },
  defaultRadius: 'md',
  spacing: { xs: rem(8), sm: rem(12), md: rem(18), lg: rem(28), xl: rem(44) },
  respectReducedMotion: true,
  cursorType: 'pointer',
  focusRing: 'auto',
  components: {
    Title: { styles: { root: { letterSpacing: '-0.035em' } } },
    Anchor: { defaultProps: { underline: 'hover' } },
    Button: {
      defaultProps: { fw: 500 },
      styles: { root: { letterSpacing: '-0.01em', alignSelf: 'flex-start' } },
    },
    Card: { classNames: { root: 'workspace-card' } },
    InputWrapper: {
      defaultProps: { withAsterisk: false },
      styles: { label: { marginBottom: rem(5), fontWeight: 500 } },
    },
    Input: { styles: { input: { minHeight: rem(38) } } },
    Badge: { defaultProps: { tt: 'none', fw: 500 }, styles: { root: { letterSpacing: '0' } } },
  },
});

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': '#fffefa',
    '--quizee-ground': '#f8f9f6',
    '--quizee-line': '#e4e7df',
    '--quizee-soft': '#f0f3eb',
    '--quizee-muted': '#66705b',
  },
  dark: {
    '--mantine-color-body': '#252e21',
    '--quizee-ground': '#1b2218',
    '--quizee-line': '#3c4735',
    '--quizee-soft': '#303e28',
    '--quizee-muted': '#b1bba6',
  },
});
